"use client";

import styled from "@emotion/styled";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import type { ButtonProps as AntButtonProps } from "antd";
import { Button } from "antd";
import React from "react";
import { OCTOLANE_COLOR_TOKENS } from "~/app/styles/tokens";

import type { IconProp } from "@fortawesome/fontawesome-svg-core";

interface IconButtonProps extends AntButtonProps {
  iconName?: IconProp;
  ref?: React.Ref<HTMLButtonElement>;
}

const StyledIconButton = styled(Button)<{ $danger?: boolean }>`
  &.ant-btn-text {
    box-shadow: none;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    color: ${OCTOLANE_COLOR_TOKENS.text.light};
    &:focus-visible {
      outline: 2px solid ${OCTOLANE_COLOR_TOKENS.border.base} !important;
    }
    &:focus {
      outline: none;
    }
    &:hover {
      background: ${({ $danger }) =>
        $danger
          ? OCTOLANE_COLOR_TOKENS.error.lighter
          : OCTOLANE_COLOR_TOKENS.bg.lighter} !important;
      color: ${({ $danger }) =>
        $danger
          ? OCTOLANE_COLOR_TOKENS.error.base
          : OCTOLANE_COLOR_TOKENS.text.base} !important;

      & > .ant-btn-icon {
        & > svg {
          color: ${({ $danger }) =>
            $danger
              ? OCTOLANE_COLOR_TOKENS.error.base
              : OCTOLANE_COLOR_TOKENS.text.base} !important;
        }

        & > .ant-badge > svg {
          color: ${({ $danger }) =>
            $danger
              ? OCTOLANE_COLOR_TOKENS.error.base
              : OCTOLANE_COLOR_TOKENS.text.light} !important;
        }
      }
    }
    & > .ant-btn-icon {
      width: 16px !important;
      height: 16px !important;
      margin-inline-end: 0px !important;
      display: flex;
      align-items: center;

      & > svg {
        width: 16px !important;
        height: 16px !important;
      }

      & > .ant-badge {
        width: 16px !important;
        height: 16px !important;
        color: ${OCTOLANE_COLOR_TOKENS.text.lighter} !important;
        display: flex;

        & > svg {
          width: 16px !important;
          height: 16px !important;
          transition: color 300ms ease-in-out;
        }

        & > sup {
          font-size: 10px !important;
          background: ${OCTOLANE_COLOR_TOKENS.bg.base} !important;
        }
      }
    }
  }
  &.ant-btn-default {
    box-shadow: none;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    color: ${OCTOLANE_COLOR_TOKENS.text.light} !important;
    border: 1px solid ${OCTOLANE_COLOR_TOKENS.border.light} !important;
    box-shadow: 0px 1px 2px 0px ${OCTOLANE_COLOR_TOKENS.border.light} !important;
    &:focus-visible {
      outline: 2px solid ${OCTOLANE_COLOR_TOKENS.border.base} !important;
      outline-offset: 2px;
    }
    &:focus {
      outline: none;
    }
    &:hover {
      background: ${OCTOLANE_COLOR_TOKENS.bg.lighter} !important;
      border-color: ${OCTOLANE_COLOR_TOKENS.border.light} !important;
      color: ${OCTOLANE_COLOR_TOKENS.text.light} !important;
      box-shadow: none !important;

      & > .ant-btn-icon {
        & > svg {
          color: ${OCTOLANE_COLOR_TOKENS.text.base} !important;
        }

        & > .ant-badge > svg {
          color: ${OCTOLANE_COLOR_TOKENS.text.light} !important;
        }
      }
    }

    & > .ant-btn-icon {
      width: 16px !important;
      height: 16px !important;
      margin-inline-end: 0px !important;

      & > svg {
        width: 16px !important;
        height: 16px !important;
        transition: color 300ms ease-in-out;
        color: ${OCTOLANE_COLOR_TOKENS.text.light} !important;
      }

      & > .ant-badge {
        width: 12px !important;
        height: 12px !important;
        color: ${OCTOLANE_COLOR_TOKENS.text.lighter} !important;

        & > svg {
          width: 12px !important;
          height: 12px !important;
          transition: color 300ms ease-in-out;
          color: ${OCTOLANE_COLOR_TOKENS.text.light} !important;
        }

        & > sup {
          font-size: 10px !important;
          background: ${OCTOLANE_COLOR_TOKENS.bg.base} !important;
        }
      }
    }
  }
  &.ant-btn-lg {
    height: 32px !important;
    width: 32px !important;
    font-size: 14px !important;
  }
  &.ant-btn-sm {
    height: 24px !important;
    width: 24px !important;
    font-size: 12px !important;

    & > .ant-btn-icon {
      width: 12px !important;
      height: 12px !important;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-inline-end: 0px !important;

      & > svg {
        width: 12px !important;
        height: 12px !important;
        transition: color 300ms ease-in-out;
        color: ${OCTOLANE_COLOR_TOKENS.text.light} !important;
      }
    }
  }
`;

const IconButton: React.FC<IconButtonProps> = ({
  iconPosition = "start",
  size = "middle",
  iconName,
  type = "text",
  ...rest
}) => {
  return (
    <StyledIconButton
      type={type}
      size={size}
      icon={
        iconName ? <FontAwesomeIcon icon={iconName} className="size-3" /> : null
      }
      $danger={rest.danger}
      iconPosition={iconPosition}
      {...rest}
    />
  );
};

export default IconButton;
